import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AnimationCard from '../components/AnimationCard';
import ReportModal from '../components/ReportModal';
import VrmaPreview from '../components/viewer/VrmaPreview';
import Spinner from '../components/Spinner';
import {
  fetchAnimation,
  fetchAnimations,
  fetchFavoriteIds,
  favoriteAnimation,
  unfavoriteAnimation,
  fetchFollowStatus,
  followUser,
  unfollowUser,
  countAnimation,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { decryptToBlobUrl } from '../lib/protectedAsset';
import { relatedTo } from '../lib/recommend';
import { detectStorePlatform } from '../lib/storePlatform';
import { USAGE_TERMS_LEAD_TEXT, usageSummaryRows } from '../lib/usageTermsSummary';
import type { Animation } from '../types';

/** 「関連ポーズ」セクションの初期表示件数、および追加読み込み1回あたりの件数。 */
const RELATED_PAGE_SIZE = 12;

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const { user, login } = useAuth();
  const [animation, setAnimation] = useState<Animation | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [likes, setLikes] = useState(0);
  const [favorited, setFavorited] = useState(false);
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);
  const [resolvedVrmaUrl, setResolvedVrmaUrl] = useState<string | undefined>(undefined);
  const [decryptError, setDecryptError] = useState('');
  // 再生数カウントの二重送信防止（1マウントにつき1回だけ送る）。
  const playCountedRef = useRef(false);
  // お気に入りトグルの連打対策。リクエスト中の再入は無視する。
  const favoriteBusyRef = useRef(false);

  // ---- 関連ポーズ ---------------------------------------------------------
  // 「関連ポーズ」の母集団。Animations.tsx のトップビューと同じく、公開一覧を1回だけ
  // まとめて取得する（カーソルを辿って全件は取りにいかない）。
  const [relatedPool, setRelatedPool] = useState<Animation[]>([]);
  const [relatedVisibleCount, setRelatedVisibleCount] = useState(RELATED_PAGE_SIZE);
  const relatedSentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchAnimations()
      .then(({ items }) => {
        if (!cancelled) setRelatedPool(items);
      })
      .catch(() => {
        // 取得失敗時はセクションごと非表示にするだけにとどめ、詳細ページ本体は壊さない。
        if (!cancelled) setRelatedPool([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // id（表示対象）が変わるたびに読み込み件数を初期値へ戻す。
  useEffect(() => {
    setRelatedVisibleCount(RELATED_PAGE_SIZE);
  }, [id]);

  const relatedItems = useMemo(
    () => (animation ? relatedTo(animation, relatedPool) : []),
    [animation, relatedPool],
  );
  const relatedVisibleItems = relatedItems.slice(0, relatedVisibleCount);
  const relatedHasMore = relatedVisibleCount < relatedItems.length;

  // sentinel（一覧末尾のダミー要素）が画面に入ったら追加で RELATED_PAGE_SIZE 件表示する。
  useEffect(() => {
    if (!relatedHasMore) return;
    const el = relatedSentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setRelatedVisibleCount((c) => c + RELATED_PAGE_SIZE);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [relatedHasMore]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetchAnimation(id)
      .then((a) => {
        if (!a) {
          setNotFound(true);
        } else {
          setAnimation(a);
          setLikes(a.likes);
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  // encrypted な保護アセット（公式サンプル等）は暗号文の URL しか持たないため、
  // 再生前に鍵で復号して blob URL 化する。取得した blob URL はローダー側でキャッシュされ、
  // 同じアニメーションを何度開いても再利用されるので、ここでは URL.revokeObjectURL しない。
  useEffect(() => {
    setDecryptError('');
    if (!animation) return;
    if (animation.encrypted && animation.vrmaUrl) {
      let cancelled = false;
      decryptToBlobUrl(animation.vrmaUrl)
        .then((url) => {
          if (!cancelled) setResolvedVrmaUrl(url);
        })
        .catch((e) => {
          if (!cancelled) setDecryptError(String(e));
        });
      return () => {
        cancelled = true;
      };
    }
    setResolvedVrmaUrl(animation.vrmaUrl);
  }, [animation]);

  // お気に入り済みかどうかはログイン時のみ分かる。
  useEffect(() => {
    if (!id || !user) return;
    fetchFavoriteIds().then((ids) => setFavorited(ids.has(id)));
  }, [id, user]);

  const isOwner = !!user && !!animation?.ownerId && user.sub === animation.ownerId;

  // 再生数カウント: ビューアに再生可能な VRMA が渡ったタイミング（vrmaUrl 解決後）で1回だけ送る。
  // 自分の投稿・非公開のものはカウントしない（VrmaPreview 自体に再生開始コールバックが無いため、
  // ここでは「再生対象の URL がビューアに渡った」ことをもって再生開始とみなす）。
  useEffect(() => {
    if (!id || !animation || !resolvedVrmaUrl || playCountedRef.current) return;
    if (isOwner) return;
    if ((animation.visibility ?? 'public') === 'private') return;
    playCountedRef.current = true;
    countAnimation(id, 'play');
  }, [id, animation, resolvedVrmaUrl, isOwner]);

  // フォロー中かどうかはログイン中かつ他人の投稿のときだけ分かる。
  useEffect(() => {
    const ownerId = animation?.ownerId;
    if (!ownerId || !user || isOwner) return;
    fetchFollowStatus(ownerId).then(setFollowing);
  }, [animation?.ownerId, user, isOwner]);

  const toggleFavorite = () => {
    if (!id) return;
    if (!user) {
      login();
      return;
    }
    if (favoriteBusyRef.current) return;
    favoriteBusyRef.current = true;

    const isFav = favorited;
    const prevLikes = likes;
    setMessage('');
    // 楽観的更新: API 応答を待たずに即座に反映する。
    setFavorited(!isFav);
    setLikes(prevLikes + (isFav ? -1 : 1));

    (async () => {
      try {
        const res = isFav ? await unfavoriteAnimation(id) : await favoriteAnimation(id);
        // サーバーが返した正値で上書きする。
        setFavorited(res.favorited);
        setLikes(res.likes);
      } catch (e) {
        // 楽観的更新をロールバックする。
        setFavorited(isFav);
        setLikes(prevLikes);
        setMessage(String(e));
      } finally {
        favoriteBusyRef.current = false;
      }
    })();
  };

  const toggleFollow = async () => {
    const ownerId = animation?.ownerId;
    if (!ownerId) return;
    if (!user) {
      login();
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      const res = following ? await unfollowUser(ownerId) : await followUser(ownerId);
      setFollowing(res.following);
    } catch (e) {
      setMessage(String(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <Spinner />
      </div>
    );
  }

  if (notFound || !animation) {
    return (
      <div className="container">
        <h2>アニメーションが見つかりません</h2>
        <Link to="/" className="btn">
          一覧へ戻る
        </Link>
      </div>
    );
  }

  const isPrivate = (animation.visibility ?? 'public') === 'private';
  const isSensitive = animation.rating === 'sensitive';
  const storeUrls = animation.credit?.storeUrls ?? [];

  return (
    <div className="container">
      <div className="detail">
        <div>
          <VrmaPreview vrmaUrl={resolvedVrmaUrl} />
          {!animation.vrmaUrl && (
            <div className="notice" style={{ marginTop: 12 }}>
              このサンプルには .vrma ファイルが添付されていません。プレビュー枠の「VRMA ファイルを選択」から
              手元の .vrma を読み込むと再生できます。
            </div>
          )}
          {decryptError && (
            <div className="viewer__error" style={{ marginTop: 12 }}>
              保護アセットの復号に失敗しました。{decryptError}
            </div>
          )}
        </div>

        <aside className="detail__side">
          <h1>
            {animation.title}
            {isPrivate && <span className="badge badge--private">非公開</span>}
            {isSensitive && <span className="badge badge--sensitive">センシティブ</span>}
          </h1>
          <div className="detail__author" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {animation.ownerId ? (
              <Link to={`/users/${animation.ownerId}`}>@{animation.author}</Link>
            ) : (
              <>@{animation.author}</>
            )}
            {animation.ownerId && !isOwner && (
              <button
                type="button"
                className={`btn${following ? ' btn--primary' : ''}`}
                onClick={toggleFollow}
                disabled={busy}
              >
                {!user ? 'ログインしてフォロー' : following ? 'フォロー中' : 'フォロー'}
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '8px 0' }}>
            <button
              className={`btn${favorited ? ' btn--primary' : ''}`}
              onClick={toggleFavorite}
              disabled={busy}
              title={user ? undefined : 'お気に入りにはログインが必要です'}
            >
              {favorited ? '♥' : '♡'} お気に入り {likes}
            </button>
            {storeUrls.map((url) => {
              const p = detectStorePlatform(url);
              if (!p) return null;
              return (
                <a
                  key={url}
                  className="btn"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  🛒 {p.label}
                </a>
              );
            })}
            {isOwner && (
              <Link to={`/animations/${animation.id}/edit`} className="btn">
                ✏ 編集
              </Link>
            )}
          </div>

          {message && <div className="stat">{message}</div>}

          <div className="tags">
            {animation.category && (
              <Link
                to={`/?${new URLSearchParams({ view: 'list', category: animation.category }).toString()}`}
                className="tag tag--category"
              >
                {animation.category}
              </Link>
            )}
            {animation.tags.map((t) => (
              <Link
                to={`/?${new URLSearchParams({ view: 'list', q: '#' + t }).toString()}`}
                className="tag"
                key={t}
              >
                #{t}
              </Link>
            ))}
          </div>

          <p className="detail__desc">{animation.description}</p>

          <div className="stat">
            投稿日: {new Date(animation.createdAt).toLocaleDateString('ja-JP')}
          </div>

          {animation.credit && (
            <section
              className="credit"
              style={{ marginTop: 16, padding: 12, border: '1px solid var(--color-border)', borderRadius: 8, fontSize: '0.9em' }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4 }}>クレジット</div>
              {/* 各項目は任意。名前が無ければ行自体を出さず、URL がある場合のみリンク化する。 */}
              {animation.credit.author && <div>著作権者: {animation.credit.author}</div>}
              {animation.credit.license && (
                <div>
                  ライセンス:{' '}
                  {animation.credit.licenseUrl ? (
                    <a
                      href={animation.credit.licenseUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ whiteSpace: 'pre-wrap' }}
                    >
                      {animation.credit.license}
                    </a>
                  ) : (
                    <span style={{ whiteSpace: 'pre-wrap' }}>{animation.credit.license}</span>
                  )}
                </div>
              )}
              {animation.credit.notice && (
                <div style={{ marginTop: 4, opacity: 0.8 }}>{animation.credit.notice}</div>
              )}
            </section>
          )}

          {animation.usageTerms && (
            <section
              className="upload-card"
              style={{ marginTop: 16, padding: 12, border: '1px solid var(--color-border)', borderRadius: 8 }}
            >
              <h2 className="upload-card__title">アニメーション登録者以外の利用</h2>
              <p className="upload-usage-sub">{USAGE_TERMS_LEAD_TEXT}</p>
              <ul className="upload-usage-summary">
                {usageSummaryRows(animation.usageTerms).map((row) => (
                  <li key={row.label}>
                    <span>{row.label}</span>
                    <span className={row.ok ? 'is-ok' : 'is-ng'}>
                      {row.ok ? (row.okText ?? 'OK') : (row.ngText ?? 'NG')}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div style={{ marginTop: 20 }}>
            <Link to="/" className="btn">
              ← 一覧へ戻る
            </Link>
          </div>

          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              className="modal__link"
              style={{ fontSize: 12 }}
              onClick={() => setShowReportModal(true)}
            >
              このアニメーションを通報する
            </button>
          </div>
        </aside>
      </div>

      {relatedVisibleItems.length > 0 && (
        <section className="detail-related">
          <div className="detail-related__head section-title">
            <h2>関連ポーズ</h2>
          </div>
          <div className="grid">
            {relatedVisibleItems.map((a) => (
              <AnimationCard key={a.id} animation={a} />
            ))}
          </div>
          {relatedHasMore && (
            <div ref={relatedSentinelRef} className="detail-related__loadmore">
              <Spinner size="sm" inline label="スクロールでさらに読み込み" />
            </div>
          )}
        </section>
      )}

      {id && (
        <ReportModal
          open={showReportModal}
          onClose={() => setShowReportModal(false)}
          animationId={id}
          loggedIn={!!user}
        />
      )}
    </div>
  );
}
